import * as React from "react";
import {
  WindowWithChatbot,
  ClientChatbotStore,
  ConversationProperties,
  ConversationIntent,
  IntentTypes,
} from "src/stores/chatbotStore/ClientChatbotStore";
import { BlossomCustomerNotification } from "__generated__/api";
import { sendAnalytics } from "src/components/shared/CustomerNotifications/NotificationsWithDialog/NotificationQueryUtils";
import { UitkHeading, UitkText } from "uitk-react-text";
import { UitkLayoutFlex, UitkLayoutFlexItem, UitkLayoutFlexItemProps } from "uitk-react-layout-flex";
import { UitkCardAloha, UitkCardAlohaLink } from "uitk-react-cards";
import Links = BlossomCustomerNotification.Links;
import Body = BlossomCustomerNotification.Body;
import Actions = BlossomCustomerNotification.Actions;
import { observer, inject } from "mobx-react";
import { UitkFigure, UitkFigureAspectRatioType, UitkImage } from "uitk-react-experimental-images";
import { CovidBannerVersions } from "./ReasonsToBelieveWrapper";
import { UitkSpacing } from "uitk-react-spacing";
import CustomerNotificationPhraseLinkNode = BlossomCustomerNotification._CustomerNotificationPhraseLinkNodeInlineFragment;

interface ReasonsToBelieveCardProps {
  chatbot?: ClientChatbotStore;
  link?: Links;
  body?: Body;
  analyticsCallback?: (rfrr: string, linkName: string | null) => void;
  index: number;
  minWidth: UitkLayoutFlexItemProps["minWidth"];
  maxWidth: UitkLayoutFlexItemProps["maxWidth"];
  version: CovidBannerVersions;
}

interface CardProperties {
  id: string | null | undefined;
  url: string;
  actions: Actions[] | null | undefined;
  text: string | undefined;
  subText: string;
  iconName: string;
}

export const ReasonsToBelieveCard: React.FC<ReasonsToBelieveCardProps> = inject("chatbot")(
  observer((props: ReasonsToBelieveCardProps) => {
    const { link, analyticsCallback, chatbot, maxWidth, minWidth, index, version, body } = props;
    const refundId = "openChatbot";
    const cancelTripId = "cancelChangeTrip";
    const manageYourTripId = "covidManageYourTrip";
    const reedemCreditCouponId = "covidRedeemCredits";

    const firstBodyItem = body?.items[0] as CustomerNotificationPhraseLinkNode;
    const secondBodyItem = body?.items[1] as CustomerNotificationPhraseLinkNode;
    const thirdBodyItem = body?.items[2] as CustomerNotificationPhraseLinkNode;

    const card: CardProperties = {
      id: link?.id || thirdBodyItem?.id,
      url: link?.url || thirdBodyItem?.url,
      actions: link?.actions || thirdBodyItem?.actions,
      text: link?.text || firstBodyItem?.text,
      subText: secondBodyItem?.text || "",
      iconName: thirdBodyItem?.icon?.id || "",
    };

    const useChatbot = card.id === refundId || card.id === cancelTripId || card.id === manageYourTripId;
    const isCouponCard = card.id === reedemCreditCouponId;
    const images = [
      "https://forever.travel-assets.com/flex/flexmanager/images/2020/07/09/calendar_busy.svg",
      "https://forever.travel-assets.com/flex/flexmanager/images/2020/07/09/voucher.svg",
      "https://forever.travel-assets.com/flex/flexmanager/images/2020/07/09/approved.svg",
      "https://forever.travel-assets.com/flex/flexmanager/images/2020/11/04/coupons_cut.svg",
    ];

    const imagesV3 = {
      calendar_busy: "https://forever.travel-assets.com/flex/flexmanager/images/2020/07/09/calendar_busy.svg",
      coupons_cut: "https://forever.travel-assets.com/flex/flexmanager/images/2020/11/04/coupons_cut.svg",
      suitcase: "https://forever.travel-assets.com/flex/flexmanager/images/2020/11/20/suitcase.svg",
    };

    const imagesV4 = {
      pencil: "https://forever.travel-assets.com/flex/flexmanager/images/2021/04/08/icon__mode_edit.svg",
      coin: "https://forever.travel-assets.com/flex/flexmanager/images/2021/04/08/icon__monetization_on.svg",
      chat: "https://forever.travel-assets.com/flex/flexmanager/images/2021/04/08/icon__chat.svg",
    };

    const refundIntent: ConversationIntent = {
      intent: IntentTypes.REFUND_STATUS,
    };
    const cancelIntent = {
      intent: IntentTypes.VIEW_BOOKING,
    };

    const conversationProperties: ConversationProperties = {
      launchPoint: "ReasonsToBelieve",
      pageName: "StoreFront",
    };

    /* istanbul ignore next */
    const onChatbotClick = async (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      if (analyticsCallback) {
        sendAnalytics(card.actions as Actions[], analyticsCallback);
      }
      if (chatbot && useChatbot) {
        const initialized = await chatbot.isVirtualAgentInitialized(window as WindowWithChatbot);
        if (initialized) {
          const intentMessage = refundId === card.id ? refundIntent : cancelIntent;
          const conversationStarted = await chatbot.startConversation(conversationProperties, intentMessage);
          if (!conversationStarted) {
            window.location.href = card.url;
          }

          return;
        }
      }
      window.location.href = card.url;
    };

    const cardV1 = (
      <UitkCardAloha padded>
        <UitkLayoutFlex alignItems="center" space="six">
          <UitkLayoutFlexItem minWidth={48} shrink={0}>
            <div>
              <UitkFigure ratio={UitkFigureAspectRatioType.R1_1}>
                <UitkImage src={images[index]} alt={card.text} />
              </UitkFigure>
            </div>
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem grow={1}>
            <UitkHeading tag="h3" size={5}>
              {card.text}
            </UitkHeading>
          </UitkLayoutFlexItem>
        </UitkLayoutFlex>
        <UitkCardAlohaLink>
          <a onClick={onChatbotClick} href="#">
            {card.text}
          </a>
        </UitkCardAlohaLink>
      </UitkCardAloha>
    );

    const cardV2 = (
      <UitkCardAloha border>
        <UitkSpacing padding={{ block: "four", inline: "three" }}>
          <div>
            <UitkLayoutFlex alignItems="center" justifyContent="space-between">
              <UitkLayoutFlexItem>
                <UitkHeading tag="h3" size={5}>
                  {card.text}
                </UitkHeading>
              </UitkLayoutFlexItem>
            </UitkLayoutFlex>
            <UitkSpacing padding={{ blockstart: "one" }}>
              <div>
                <UitkText size={200}>{card.subText}</UitkText>
              </div>
            </UitkSpacing>
          </div>
        </UitkSpacing>
        <UitkCardAlohaLink>
          <a onClick={onChatbotClick} href="#">
            {card.text}
          </a>
        </UitkCardAlohaLink>
      </UitkCardAloha>
    );

    const cardV2WithCoupons = (
      <UitkCardAloha border padded>
        <UitkSpacing padding={{ blockend: "two", blockstart: "two" }}>
          <UitkLayoutFlex>
            <UitkLayoutFlexItem minWidth={65} grow={1}>
              <div>
                <UitkFigure ratio={UitkFigureAspectRatioType.R1_1}>
                  <UitkImage src={images[3]} alt={card.text} />
                </UitkFigure>
              </div>
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem grow={1}>
              <UitkSpacing padding={{ inlinestart: "two" }}>
                <div>
                  <UitkHeading tag="h3" size={5}>
                    {card.text}
                  </UitkHeading>
                  <UitkSpacing padding={{ blockstart: "one" }}>
                    <div>
                      <UitkText size={200}>{card.subText}</UitkText>
                    </div>
                  </UitkSpacing>
                </div>
              </UitkSpacing>
            </UitkLayoutFlexItem>
          </UitkLayoutFlex>
        </UitkSpacing>
        <UitkCardAlohaLink>
          <a onClick={onChatbotClick} href="#">
            {card.text}
          </a>
        </UitkCardAlohaLink>
      </UitkCardAloha>
    );

    const cardV3 = (
      <UitkCardAloha border padded>
        <UitkSpacing padding={{ blockend: "two", blockstart: "two" }}>
          <UitkLayoutFlex>
            <UitkLayoutFlexItem minWidth={48} shrink={0}>
              <div>
                <UitkFigure ratio={UitkFigureAspectRatioType.R1_1}>
                  <UitkImage src={imagesV3[card?.iconName]} />
                </UitkFigure>
              </div>
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem shrink={1}>
              <UitkSpacing padding={{ inlinestart: "two" }}>
                <div>
                  <UitkHeading tag="h3" size={5}>
                    {card.text}
                  </UitkHeading>
                  <UitkSpacing padding={{ blockstart: "one" }}>
                    <div>
                      <UitkText size={300}>{card.subText}</UitkText>
                    </div>
                  </UitkSpacing>
                </div>
              </UitkSpacing>
            </UitkLayoutFlexItem>
          </UitkLayoutFlex>
        </UitkSpacing>
        <UitkCardAlohaLink>
          <a onClick={onChatbotClick} href={card.url} target="_blank" rel="noopener noreferrer">
            {card.text}
          </a>
        </UitkCardAlohaLink>
      </UitkCardAloha>
    );

    const cardV4 = (
      <UitkCardAloha border padded>
        <UitkSpacing padding={{ blockend: "one", blockstart: "one" }}>
          <UitkLayoutFlex>
            <UitkLayoutFlexItem shrink={1} minWidth="full_width">
              <div>
                <UitkHeading tag="h3" size={5}>
                  {card.text}
                </UitkHeading>
                <UitkSpacing padding={{ blockstart: "two" }}>
                  <div>
                    <UitkText size={200}>{card.subText}</UitkText>
                  </div>
                </UitkSpacing>
              </div>
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem minWidth={16} maxWidth={16} shrink={0} alignSelf={"start"}>
              <div>
                <UitkFigure ratio={UitkFigureAspectRatioType.R1_1}>
                  <UitkImage src={imagesV4[card?.iconName]} alt="" />
                </UitkFigure>
              </div>
            </UitkLayoutFlexItem>
          </UitkLayoutFlex>
        </UitkSpacing>
        <UitkCardAlohaLink>
          <a onClick={onChatbotClick} href="#">
            {card.text}
          </a>
        </UitkCardAlohaLink>
      </UitkCardAloha>
    );

    return (
      <UitkLayoutFlexItem grow={1} maxWidth={maxWidth} minWidth={minWidth}>
        {version === CovidBannerVersions.ONE && cardV1}
        {version === CovidBannerVersions.TWO && isCouponCard && cardV2WithCoupons}
        {version === CovidBannerVersions.TWO && cardV2}
        {version === CovidBannerVersions.THREE && cardV3}
        {version === CovidBannerVersions.FOUR && cardV4}
      </UitkLayoutFlexItem>
    );
  })
);
